// components/TonnagePanelTable.tsx
import React, { useState } from 'react';
import styled from 'styled-components';
import { PanelData } from '../lib/tonnageAggregation';

const Wrapper = styled.div`
  font-size: 12px;
  border: 1px solid #d9d9d9;
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
`;

const Th = styled.th<{ align?: 'left' | 'right' }>`
  text-align: ${(p) => p.align ?? 'right'};
  padding: 6px 8px;
  border-bottom: 1px solid #d9d9d9;
  background: #fafafa;
  font-weight: 700;
  white-space: nowrap;
`;

const Td = styled.td<{ align?: 'left' | 'right'; indent?: boolean }>`
  text-align: ${(p) => p.align ?? 'right'};
  padding: 3px 8px;
  padding-left: ${(p) => (p.indent ? '22px' : '8px')};
  white-space: nowrap;
`;

const AirlineRow = styled.tr<{ striped: boolean }>`
  background: ${(p) => (p.striped ? '#f5f5f5' : 'transparent')};
`;

const UnitRow = styled.tr`
  cursor: pointer;
  font-weight: 700;
  &:hover { background: #f2f2f2; }
`;

const GrandTotalRow = styled.tr`
  font-weight: 700;
  background: #d9ead3;
`;

const Delta = styled.span<{ positive: boolean }>`
  color: ${(p) => (p.positive ? '#2e7d32' : '#c62828')};
  margin-left: 4px;
`;

function formatNumber(n: number): string {
    return Math.round(n).toLocaleString('en-US');
}

function formatPct(pct: number | null): React.ReactNode {
    if (pct === null) return <span style={{ color: '#c9a227' }}>—</span>;
    const positive = pct >= 0;
    return <Delta positive={positive}>{positive ? '▲' : '▼'} {(pct * 100).toFixed(0)}%</Delta>;
}

function formatDiff(diff: number): React.ReactNode {
    if (diff === 0) return formatNumber(diff);
    const positive = diff > 0;
    return <Delta positive={positive}>{formatNumber(Math.abs(diff))}</Delta>;
}

interface Props {
    title: string;       // "Current Week" | "Current Month" | "Current Year"
    y1Label: string;      // e.g. "2025"
    y2Label: string;      // e.g. "2026"
    data: PanelData;
}

export function TonnagePanelTable({ title, y1Label, y2Label, data }: Props) {
    const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

    const toggle = (unit: string) => {
        setCollapsed((prev) => {
            const next = new Set(prev);
            next.has(unit) ? next.delete(unit) : next.add(unit);
            return next;
        });
    };

    return (
        <Wrapper>
            <Table>
                <thead>
                    <tr>
                        <Th align="left">{title} ▾</Th>
                        <Th>{y1Label}</Th>
                        <Th>{y2Label}</Th>
                        <Th colSpan={2}>Act Vs Prior {title.replace('Current ', '')}</Th>
                    </tr>
                </thead>
                <tbody>
                    {(() => {
                        let rowIndex = 0;
                        return data.groups.map((group) => {
                            const isCollapsed = collapsed.has(group.unit);
                            return (
                                <React.Fragment key={group.unit}>
                                    <UnitRow onClick={() => toggle(group.unit)}>
                                        <Td align="left">{isCollapsed ? '+' : '−'} {group.unit}</Td>
                                        <Td>{formatNumber(group.y1Total)}</Td>
                                        <Td>{formatNumber(group.y2Total)}</Td>
                                        <Td>{formatDiff(group.diff)}</Td>
                                        <Td>{formatPct(group.pct)}</Td>
                                    </UnitRow>
                                    {!isCollapsed && group.airlines.map((row) => {
                                        const striped = rowIndex % 2 === 1;
                                        rowIndex += 1;
                                        return (
                                            <AirlineRow key={row.airlineCode} striped={striped}>
                                                <Td align="left" indent>{row.airlineCode}</Td>
                                                <Td>{row.y1 ? formatNumber(row.y1) : ''}</Td>
                                                <Td>{row.y2 ? formatNumber(row.y2) : ''}</Td>
                                                <Td>{formatDiff(row.diff)}</Td>
                                                <Td>{formatPct(row.pct)}</Td>
                                            </AirlineRow>
                                        );
                                    })}
                                </React.Fragment>
                            );
                        });
                    })()}
                    <GrandTotalRow>
                        <Td align="left">Grand Total</Td>
                        <Td>{formatNumber(data.grandTotal.y1)}</Td>
                        <Td>{formatNumber(data.grandTotal.y2)}</Td>
                        <Td>{formatDiff(data.grandTotal.diff)}</Td>
                        <Td>{formatPct(data.grandTotal.pct)}</Td>
                    </GrandTotalRow>
                </tbody>
            </Table>
        </Wrapper>
    );
}